import { Loading } from '@arcadiapower/shrike';
import { BillingMachine } from '~/machines/billing-selection';
import { EnrollmentMachine } from '~/machines/enrollment-selection';
import { LegalMachine } from '~/machines/legal-completion';
import { CalendlyMachine } from '~/machines/schedule-meet';
import { UserMachine } from '~/machines/user-creation';
import { UtilityMachine } from '~/machines/utility-selection';
import { MachineScreenStates, ScreenMap } from '~/machines/utils/types';
import { AccountNumberContainer } from '~/screens/account-number/account-number.container';
import { BillingSelection } from '~/screens/billing-selection';
import { BillingContainer } from '~/screens/billing/billing.container';
import { Calendly } from '~/screens/calendly';
import { CreateAccountGoogleContainer } from '~/screens/create-account-google/create-account-google.container';
import { CreateAccountContainer } from '~/screens/create-account/create-account.container';
import { CredentialsContainer } from '~/screens/credentials/credentials.container';
import { CsUnavailableContainer } from '~/screens/cs-unavailable/cs-unavailable.container';
import { Friction, Unexpected } from '~/screens/errors';
import { PaymentHoldContainer } from '~/screens/payment-hold/payment-hold.container';
import { SelectUtilityContainer } from '~/screens/select-utility/select-utility.container';
import { SmbTerms } from '~/screens/smb-terms';
import { SmbFunnelMachine } from './types';

export type SmbScreenMap = {
  billing: ScreenMap<SmbFunnelMachine, BillingMachine>;
  billingExperience: ScreenMap<SmbFunnelMachine>;
  completed: ScreenMap<SmbFunnelMachine>;
  enrollment: ScreenMap<SmbFunnelMachine, EnrollmentMachine>;
  fetchBillingExperienceOptions: ScreenMap<SmbFunnelMachine>;
  legal: ScreenMap<SmbFunnelMachine, LegalMachine>;
  unexpected: ScreenMap<SmbFunnelMachine>;
  csUnavailable: ScreenMap<SmbFunnelMachine>;
  user: ScreenMap<SmbFunnelMachine, UserMachine>;
  utility: ScreenMap<SmbFunnelMachine, UtilityMachine>;
  calendly: ScreenMap<SmbFunnelMachine, CalendlyMachine>;
};

export const smbScreenMap: Readonly<SmbScreenMap> = {
  utility: {
    utility: SelectUtilityContainer,
    invalidProjectId: () => <CsUnavailableContainer />,
    bouncer: () => <Loading />,
    friction: () => <Friction />,
    redirect: () => <Loading />,
    completed: () => <Loading />,
  },
  user: {
    microLearning: () => <Loading />,
    user: CreateAccountContainer,
    google: CreateAccountGoogleContainer,
    completed: () => <Loading />,
  },
  billingExperience: ({ funnel: { context, send }, overrideCopy }) => (
    <BillingSelection
      context={{
        options: context.billingExperienceOptions!,
        utility: context.funnelPacket!.utility,
      }}
      next={data =>
        send({ type: 'SELECTED_BILLING_OPTION', data: { billingSelection: data.selection } })
      }
      overrideCopy={overrideCopy}
    />
  ),
  enrollment: {
    utilityConnect: CredentialsContainer,
    accountNumber: AccountNumberContainer,
    billUpload: () => <Unexpected reason="Bill upload enrollment not allowed for SMBs" />,
    fallBackAccountNumber: ({ micro, overrideCopy, funnel }) => (
      <AccountNumberContainer
        micro={micro}
        funnel={funnel}
        overrideCopy={overrideCopy}
        overrideProps={{
          error: micro.context.errors.fallBackAccountNumber,
          pending: micro.matches('fallBackAccountNumber.submitting'),
        }}
      />
    ),
    nyLmiZoneOptions: () => <Loading />,
    isInLmiZone: () => <Loading />,
    completed: () => <Loading />,
    ameren: () => <Loading />,
    amerenAuthTimeout: () => <Loading />,
  },
  billing: {
    fetching: () => <Loading />,
    payment: ({ funnel, micro, overrideCopy }) => (
      <BillingContainer
        micro={micro}
        funnel={funnel}
        overrideCopy={overrideCopy}
        overrideProps={{
          skip: () => funnel.send({ type: 'SKIP_BILLING' }),
        }}
      />
    ),
    hold: PaymentHoldContainer,
    completed: () => <Loading />,
  },
  /* eslint-disable no-unsafe-optional-chaining */
  calendly: {
    review: ({ micro: { context, send }, overrideCopy }) => {
      const { email, firstName, lastName, phoneNumber } = context?.funnelPacket?.user;
      const funnelPacket = context?.funnelPacket;
      const utmParams = context?.utmParams;
      return (
        <Calendly
          context={{
            email,
            firstName,
            lastName,
            phoneNumber,
            funnelPacket,
            utmParams,
          }}
          next={() => send({ type: 'SCHEDULE_MEET' })}
          overrideCopy={overrideCopy}
        />
      );
    },
    completed: () => <Loading />,
  },
  /* eslint-disable no-unsafe-optional-chaining */
  legal: {
    review: ({ micro: { context, matches, send }, overrideCopy }) => (
      <SmbTerms
        context={{
          billingSelection: context.billingSelection!,
          businessName: context.funnelPacket.account.businessName!,
          email: context.funnelPacket.user.email,
          firstName: context.funnelPacket.user.firstName,
          lastName: context.funnelPacket.user.lastName,
          paymentMethod: context.paymentMethod,
          utility: context.funnelPacket.utility,
        }}
        error={context.errors.review}
        loading={matches('review.fetching')}
        next={() => send({ type: 'SUBMIT_TERMS', data: {} })}
        overrideCopy={overrideCopy}
        pending={matches('review.submitting')}
      />
    ),
    completed: () => <Loading />,
  },

  fetchBillingExperienceOptions: () => <Loading />,
  completed: () => <Loading />,
  unexpected: () => <Unexpected />,
  csUnavailable: () => <CsUnavailableContainer />,
};

// error means that the map above is missing coverage for a possible state
export const _completenessCheck: Record<MachineScreenStates<SmbFunnelMachine>, any> = smbScreenMap;
